import React from "react";
import { collection, doc, onSnapshot, getDocs } from "firebase/firestore";
import Card from "../components/Card";
import CollapsibleSection from "../components/CollapsibleSection";
import { C } from "../theme";
import { db } from "../firebase";
import { useSessionContext } from "../context/SessionContext";
import { useAnglerCatalog, findById } from "../data/anglerCatalog";

const REGION_NAME_CACHE_KEY = "the99.regionNameCache";

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value?.seconds === "number") {
    const parsed = new Date(value.seconds * 1000);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function formatDate(value) {
  const date = toDate(value);
  if (!date) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatDuration(startedAt, endedAt) {
  const started = toDate(startedAt);
  const ended = toDate(endedAt);
  if (!started) return "0h 00m";
  const end = ended || new Date();
  const diffMs = Math.max(0, end.getTime() - started.getTime());
  const totalMinutes = Math.round(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

function formatTime(value) {
  const date = toDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function toMs(value) {
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return 0;
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function toStartOfDayMs(dateKey) {
  if (!dateKey) return null;
  const parsed = new Date(`${dateKey}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

function toEndOfDayMs(dateKey) {
  if (!dateKey) return null;
  const parsed = new Date(`${dateKey}T23:59:59.999`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

function formatLocation(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number") return "";
  return `${lat.toFixed(3)}, ${lng.toFixed(3)}`;
}

function toRegionKey(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number") return "";
  const bucketLat = Math.round(lat * 4) / 4; // 0.25 degree buckets (~17 mi)
  const bucketLng = Math.round(lng * 4) / 4;
  return `${bucketLat.toFixed(2)}|${bucketLng.toFixed(2)}`;
}

function regionLabelFromKey(regionKey) {
  const [latRaw, lngRaw] = String(regionKey || "").split("|");
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "Unknown Region";

  const latHem = lat >= 0 ? "N" : "S";
  const lngHem = lng >= 0 ? "E" : "W";
  return `Area near ${Math.abs(lat).toFixed(2)}°${latHem}, ${Math.abs(lng).toFixed(2)}°${lngHem}`;
}

function readRegionNameCache() {
  try {
    const raw = localStorage.getItem(REGION_NAME_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeRegionNameCache(cacheObj) {
  try {
    localStorage.setItem(REGION_NAME_CACHE_KEY, JSON.stringify(cacheObj));
  } catch {}
}

function formatRegionNameFromAddress(address = {}) {
  const city = address.city || address.town || address.village || address.hamlet || address.municipality;
  const county = address.county || address.state_district;
  const state = address.state || address.region;

  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (county && state) return `${county}, ${state}`;
  if (county) return county;
  if (state) return state;
  return "Unknown Region";
}

async function resolveRegionName(regionKey) {
  const [latRaw, lngRaw] = String(regionKey || "").split("|");
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const url =
    `https://nominatim.openstreetmap.org/reverse` +
    `?format=jsonv2&lat=${encodeURIComponent(lat)}` +
    `&lon=${encodeURIComponent(lng)}` +
    `&zoom=10&addressdetails=1`;

  const response = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Accept-Language": "en-US,en",
    },
  });

  if (!response.ok) return null;
  const payload = await response.json();
  return formatRegionNameFromAddress(payload?.address || {});
}

function getSessionDateValue(session) {
  return session.startedAt || session.endedAt || null;
}

export default function SessionsScreen({ onBack, onSelectSession }) {
  const { userId } = useSessionContext();
  const { species: speciesCatalog } = useAnglerCatalog();
  const [preferredSpeciesIds, setPreferredSpeciesIds] = React.useState([]);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [speciesFilter, setSpeciesFilter] = React.useState("");
  const [selectedRegion, setSelectedRegion] = React.useState("");
  const [sortOrder, setSortOrder] = React.useState("desc");
  const [regionNameMap, setRegionNameMap] = React.useState(() => readRegionNameCache());
  const [showFilters, setShowFilters] = React.useState(true);

  React.useEffect(() => {
    if (!userId) {
      setSessions([]);
      setLoading(false);
      return;
    }

    const sessionsRef = collection(db, "users", userId, "sessions");
    const unsub = onSnapshot(
      sessionsRef,
      async (snap) => {
        const next = await Promise.all(
          snap.docs.map(async (item) => {
            const data = item.data() || {};
            let catchCount = 0;
            let speciesSet = [];
            let locationSearch = "";
            let regionKeys = [];
            try {
              const catchesSnap = await getDocs(collection(db, "users", userId, "sessions", item.id, "catches"));
              catchCount = catchesSnap.size;
              const species = new Set();
              const locations = [];
              const regions = new Set();

              catchesSnap.docs.forEach((catchDoc) => {
                const catchData = catchDoc.data() || {};
                const normalizedSpecies = normalizeText(catchData.species);
                if (normalizedSpecies) species.add(normalizedSpecies);

                const catchLoc = catchData.location || null;
                if (typeof catchLoc?.lat === "number" && typeof catchLoc?.lng === "number") {
                  locations.push(formatLocation(catchLoc.lat, catchLoc.lng));
                  const regionKey = toRegionKey(catchLoc.lat, catchLoc.lng);
                  if (regionKey) regions.add(regionKey);
                }
              });

              speciesSet = Array.from(species);
              locationSearch = normalizeText(locations.join(" | "));
              regionKeys = Array.from(regions);
            } catch (error) {
              console.error("[Sessions] failed to load catches count:", error);
            }

            const sessionLocation = data.location || null;
            const sessionLocationText =
              typeof sessionLocation?.lat === "number" && typeof sessionLocation?.lng === "number"
                ? formatLocation(sessionLocation.lat, sessionLocation.lng)
                : "";
            const sessionRegionKey =
              typeof sessionLocation?.lat === "number" && typeof sessionLocation?.lng === "number"
                ? toRegionKey(sessionLocation.lat, sessionLocation.lng)
                : "";
            if (sessionRegionKey && !regionKeys.includes(sessionRegionKey)) {
              regionKeys.push(sessionRegionKey);
            }

            const searchLocationBlob = normalizeText([sessionLocationText, locationSearch].filter(Boolean).join(" | "));
            const searchSpeciesBlob = normalizeText(speciesSet.join(" | "));

            return {
              id: item.id,
              startedAt: data.startedAt || null,
              endedAt: data.endedAt || null,
              catchCount,
              speciesSet,
              regionKeys,
              searchSpeciesBlob,
              searchLocationBlob,
            };
          })
        );

        setSessions(next);
        setLoading(false);
      },
      (error) => {
        console.error("[Sessions] failed to load sessions:", error);
        setSessions([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [userId]);

  React.useEffect(() => {
    if (!userId) {
      setPreferredSpeciesIds([]);
      return undefined;
    }

    const unsub = onSnapshot(
      doc(db, "userPreferences", userId),
      (snap) => {
        const data = snap.exists() ? snap.data() || {} : {};
        // The angler's curated species can live under either field: the
        // dashboard "My Species" picker writes `fishSpecies`, while enrollment
        // and the Catch Log use `fishSpeciesIds`. Merge both so the filter
        // populates regardless of which screen set them.
        const toIds = (arr) =>
          Array.isArray(arr) ? arr.map((id) => String(id || "").trim()).filter(Boolean) : [];
        const ids = Array.from(new Set([...toIds(data.fishSpecies), ...toIds(data.fishSpeciesIds)]));
        setPreferredSpeciesIds(ids);
      },
      (error) => {
        console.error("[Sessions] failed to load species preferences:", error);
        setPreferredSpeciesIds([]);
      }
    );

    return () => unsub();
  }, [userId]);

  const availableSpecies = React.useMemo(() => {
    const all = new Set();
    preferredSpeciesIds.forEach((id) => {
      const label = findById(speciesCatalog, id)?.label;
      if (label) all.add(label);
    });
    return Array.from(all).sort((a, b) => a.localeCompare(b));
  }, [preferredSpeciesIds, speciesCatalog]);

  const availableRegions = React.useMemo(() => {
    const counts = new Map();
    sessions.forEach((session) => {
      const unique = new Set(session.regionKeys || []);
      unique.forEach((regionKey) => {
        counts.set(regionKey, (counts.get(regionKey) || 0) + 1);
      });
    });

    return Array.from(counts.entries())
      .map(([value, count]) => ({
        value,
        count,
        label: `${regionLabelFromKey(value)} (${count})`,
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [sessions]);

  React.useEffect(() => {
    const missing = availableRegions
      .map((region) => region.value)
      .filter((value) => value && !regionNameMap[value]);

    if (missing.length === 0) return;

    let cancelled = false;
    const resolveMissing = async () => {
      const updates = {};
      await Promise.all(
        missing.map(async (regionKey) => {
          try {
            const name = await resolveRegionName(regionKey);
            if (name) updates[regionKey] = name;
          } catch {
            // Fall back to coordinate label if lookup fails.
          }
        })
      );

      if (cancelled || Object.keys(updates).length === 0) return;
      setRegionNameMap((prev) => {
        const next = { ...prev, ...updates };
        writeRegionNameCache(next);
        return next;
      });
    };

    resolveMissing();
    return () => {
      cancelled = true;
    };
  }, [availableRegions, regionNameMap]);

  const filteredSessions = React.useMemo(() => {
    const minMs = toStartOfDayMs(startDate);
    const maxMs = toEndOfDayMs(endDate);
    const speciesNeedle = normalizeText(speciesFilter);
    const regionNeedle = selectedRegion;

    const next = sessions.filter((session) => {
      const sessionDate = toDate(getSessionDateValue(session));
      const sessionMs = sessionDate ? sessionDate.getTime() : 0;

      if (minMs != null && sessionMs < minMs) return false;
      if (maxMs != null && sessionMs > maxMs) return false;
      if (speciesNeedle && !normalizeText(session.searchSpeciesBlob).includes(speciesNeedle)) return false;
      if (regionNeedle && !(session.regionKeys || []).includes(regionNeedle)) return false;

      return true;
    });

    next.sort((a, b) => {
      const aTime = toMs(getSessionDateValue(a));
      const bTime = toMs(getSessionDateValue(b));
      if (sortOrder === "asc") return aTime - bTime;
      return bTime - aTime;
    });

    return next;
  }, [sessions, startDate, endDate, speciesFilter, selectedRegion, sortOrder]);

  const handleResetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSpeciesFilter("");
    setSelectedRegion("");
    setSortOrder("desc");
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, paddingBottom: 62, position: "relative" }}>
      <div style={{ padding: "18px 18px 12px" }}>
        <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 2 }}>TRIP HISTORY</div>
        <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>Previous Sessions</div>
      </div>

      <div style={{ margin: "0 14px 10px" }}>
        <CollapsibleSection
          open={showFilters}
          onToggle={() => setShowFilters(!showFilters)}
          headerTitle={showFilters ? "Hide search criteria" : "Show search criteria"}
          bodyStyle={{ padding: "0 12px 12px" }}
          title={<span style={{ fontSize: 10, color: C.muted, letterSpacing: 2 }}>SEARCH CRITERIA</span>}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ width: "100%" }}>
              <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>SPECIES</div>
              <select
                value={speciesFilter}
                onChange={(e) => setSpeciesFilter(e.target.value)}
                style={{
                  width: "100%",
                  height: 32,
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  background: C.cardBg,
                  color: C.white,
                  padding: "0 8px",
                  fontSize: 11,
                  boxSizing: "border-box",
                  colorScheme: "dark",
                }}
              >
                <option value="">All species</option>
                {availableSpecies.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            <div style={{ width: "100%" }}>
              <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>DATE RANGE</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    width: "100%",
                    height: 32,
                    borderRadius: 8,
                    border: `1px solid ${C.border}`,
                    background: C.cardBg,
                    color: C.white,
                    padding: "0 8px",
                    fontSize: 11,
                    boxSizing: "border-box",
                    colorScheme: "dark",
                  }}
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    width: "100%",
                    height: 32,
                    borderRadius: 8,
                    border: `1px solid ${C.border}`,
                    background: C.cardBg,
                    color: C.white,
                    padding: "0 8px",
                    fontSize: 11,
                    boxSizing: "border-box",
                    colorScheme: "dark",
                  }}
                />
              </div>
            </div>

            <div style={{ width: "100%" }}>
              <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>REGION</div>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                style={{
                  width: "100%",
                  height: 32,
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  background: C.cardBg,
                  color: C.white,
                  padding: "0 8px",
                  fontSize: 11,
                  boxSizing: "border-box",
                  colorScheme: "dark",
                }}
              >
                <option value="">All regions</option>
                {availableRegions.map((region) => {
                  const friendlyName = regionNameMap[region.value] || regionLabelFromKey(region.value);
                  return (
                    <option key={region.value} value={region.value}>{`${friendlyName} (${region.count})`}</option>
                  );
                })}
              </select>
            </div>

            <div style={{ width: "100%" }}>
              <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>SORT</div>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{
                  width: "100%",
                  height: 32,
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  background: C.cardBg,
                  color: C.white,
                  padding: "0 8px",
                  fontSize: 11,
                  boxSizing: "border-box",
                  colorScheme: "dark",
                }}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2 }}>
              <div style={{ fontSize: 10, color: C.muted }}>
                {filteredSessions.length} {filteredSessions.length === 1 ? "session" : "sessions"} found
              </div>
              <button
                type="button"
                onClick={handleResetFilters}
                style={{
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  background: C.cardBg,
                  color: C.white,
                  height: 30,
                  padding: "0 10px",
                  fontSize: 10,
                  cursor: "pointer",
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </CollapsibleSection>
      </div>

      <div
        className="hide-scrollbar"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          margin: "0 14px",
          paddingRight: 2,
          paddingBottom: 8,
        }}
      >
        {loading && (
          <Card style={{ padding: "12px 14px" }}>
            <div style={{ fontSize: 11, color: C.muted }}>Loading sessions…</div>
          </Card>
        )}

        {!loading && filteredSessions.map((session) => (
          <button
            key={session.id}
            type="button"
            onClick={() => onSelectSession?.(session.id)}
            style={{
              width: "100%",
              textAlign: "left",
              background: "transparent",
              border: "none",
              padding: 0,
              margin: 0,
              cursor: "pointer",
            }}
          >
            <Card style={{ padding: "12px 14px", marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 14, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>
                    {formatDate(getSessionDateValue(session))}
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
                    {formatDuration(session.startedAt, session.endedAt)} · {session.catchCount} catches
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                    Start: {formatTime(session.startedAt)} · End: {session.endedAt ? formatTime(session.endedAt) : "In Progress"}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: C.accent, fontWeight: 700 }}>
                  OPEN →
                </div>
              </div>
            </Card>
          </button>
        ))}

        {!loading && filteredSessions.length === 0 && (
          <Card style={{ padding: "12px 14px" }}>
            <div style={{ fontSize: 11, color: C.muted }}>
              {sessions.length === 0 ? "No sessions found yet." : "No sessions match current filters."}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
